/**
 * GMA-1618 (CompassCare Web 3/14 — Vitals triage) — sparkline component.
 *
 * A tiny SVG trend line for the Vitals detail panel's 24h vitals (HR, SpO₂, RR,
 * temp). Thin renderer over the pure `computeSparkline` geometry: an optional
 * normal-range threshold band behind the line, the polyline itself, and a
 * marker on the latest reading. Reusable anywhere a dense inline trend is
 * needed (detail drawers, cards).
 *
 * a11y: exposes `role="img"` + an `aria-label` so the trend is announced with a
 * caller-supplied description (e.g. "HR, last 24h") rather than being an
 * unlabelled decorative graphic. Fail-safe: an empty/invalid series renders a
 * labelled-but-blank svg (no polyline) instead of throwing. Colors default to
 * the muted chrome token; callers pass a severity color for at-risk trends.
 */
import React from "react";
import PropTypes from "prop-types";
import { computeSparkline } from "../../pages/web/sparklineModel";
import { theme, severity } from "../../styles/designTokens";

export default function Sparkline({
  values,
  width = 120,
  height = 32,
  band = null,
  domain = null,
  color,
  ariaLabel = "Trend",
}) {
  const geo = computeSparkline(values, { width, height, band, domain });
  const stroke = color || theme.textMuted;

  return (
    <svg
      role="img"
      aria-label={ariaLabel}
      data-testid="sparkline"
      width={geo.width}
      height={geo.height}
      viewBox={`0 0 ${geo.width} ${geo.height}`}
      style={{ display: "block", overflow: "visible" }}
    >
      {geo.band && (
        <rect
          data-testid="sparkline-band"
          x={geo.band.x}
          y={geo.band.y}
          width={geo.band.width}
          height={geo.band.height}
          fill={severity.normal.color}
          opacity={0.14}
        />
      )}
      {geo.points && (
        <polyline
          data-testid="sparkline-line"
          points={geo.points}
          fill="none"
          stroke={stroke}
          strokeWidth={1.5}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      )}
      {geo.last && (
        <circle
          data-testid="sparkline-last"
          cx={geo.last.x}
          cy={geo.last.y}
          r={2}
          fill={stroke}
        />
      )}
    </svg>
  );
}

Sparkline.propTypes = {
  // Ordered samples (oldest -> newest). Missing/non-finite entries are skipped.
  values: PropTypes.array,
  width: PropTypes.number,
  height: PropTypes.number,
  // Normal-range band highlighted behind the line (value space).
  band: PropTypes.shape({ low: PropTypes.number, high: PropTypes.number }),
  // Force the value domain (e.g. to align several sparklines to one scale).
  domain: PropTypes.shape({ min: PropTypes.number, max: PropTypes.number }),
  // Stroke color; defaults to the muted chrome token.
  color: PropTypes.string,
  // Accessible description of what the trend shows.
  ariaLabel: PropTypes.string,
};
